"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";
import Background3d from "./reactfiber";
import Image from "next/image";
import HoloCard from "./holoCard";
import TokenEvolutionCard from "./tokenEvolutionCard";
import TokenLawsSection from "./tokenLawsSection";
import OutlineHeading from "../ui/outlineHeading";
import Link from "next/link";
gsap.registerPlugin(ScrollTrigger);

// Declare custom property on Window interface
declare global {
  interface Window {
    __catdev_scrollTween?: gsap.core.Tween;
    __catdev_lenis?: Lenis | null;
  }
}

export default function HorizontalScrollSection() {
  const scrollRef = useRef<HTMLElement | null>(null);
  const panelsRef = useRef<(HTMLDivElement | null)[]>([]);
  const lenisRef = useRef<Lenis | null>(null);

  // Detect mobile devices
  // useEffect(() => {
  //   const checkMobile = () => {
  //     setIsMobile(true);
  //   };

  //   checkMobile();

  //   window.addEventListener("resize", checkMobile);
  //   return () => window.removeEventListener("resize", checkMobile);
  // }, []);

  useEffect(() => {
    // Initialize Lenis for smooth scrolling
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    gsap.ticker.add((time) => {
      lenisRef.current?.raf(time * 1000);
    });

    if (!scrollRef.current) return;

    const ctx = gsap.context(() => {
      const validPanels = panelsRef.current.filter(Boolean);
      validPanels.forEach((panel) => {
        if (panel) {
          gsap.set(panel, {
            xPercent: 0,
            minHeight: "100vh",
            width: "100%",
          });
        }
      });

      // Still create a dummy tween for DNA model compatibility
      const dummyTween = gsap.to({}, { duration: 1 });
      window.__catdev_scrollTween = dummyTween;
    }, scrollRef);

    // Update ScrollTrigger when Lenis scrolls
    lenisRef.current.on("scroll", ScrollTrigger.update);

    return () => {
      ctx.revert();
      const rafFunction = lenisRef.current?.raf;
      if (lenisRef.current) {
        lenisRef.current.destroy();
        lenisRef.current = null;
      }
      if (rafFunction) {
        gsap.ticker.remove(rafFunction);
      }
    };
  }, []);

  useEffect(() => {
    // const box = document.querySelector("#evolution-box");
    const panel2 = document.querySelector("#panel-2");

    if (!panel2) return;

    // Pin the whole panel-2 while animation plays
    ScrollTrigger.create({
      trigger: panel2,
      start: "top top",
      end: "+=200%", // keep this higher than animation to "pause"
      scrub: true,
      pin: true,
      pinSpacing: true,
    });
  }, []);

  return (
    <>
      <Background3d />
      <section
        id="hero"
        className="h-screen w-full  flex items-center justify-center text-black text-4xl font-bold"
      >
        <div className=" text-center relative z-10 md:p-0 p-2">
          {" "}
          <h1 className="text-center text-purple-100 text-2xl md:text-6xl uppercase">
            The Feral Prodigy of <br />
            Darwin&apos;s Legacy
          </h1>
          <p className="max-w-xl mx-auto mt-2 text-sm md:text-lg text-white/70">
            It is not the strongest of the species that survive, nor the most
            intelligent, but the one most responsive to change.
          </p>
        </div>
        <span className="absolute bottom-0 z-10 justify-center my-12 animate-bounce opacity-80">
        <Link href="/docs/#horizontal-scroll-container">
          <Image
            width={40}
            height={40}
            src="/docs/scroll-down.svg"
            alt={"scroll down"}
          />
          </Link>
        </span>
      </section>

      <section
        ref={scrollRef}
        className={`relative horizontal-scroll-container w-full`}
        id="horizontal-scroll-container"
      >
        <div className={`flex flex-col w-full`}>
          <div
            ref={(el) => {
              panelsRef.current[0] = el;
            }}
            id="panel-1"
            className=" xl:p-0 h-auto w-full flex flex-col items-center  md:gap-0 justify-center text-4xl bg-transparent text-white overflow-clip"
          >
            <div className="relative flex flex-col  md:flex-row items-center justify-between p-2 w-full lg:w-full md:max-w-screen-lg md:p-6 h-full">
              <div className="w-full">
                <h3 className="text-center md:text-left mb-4 text-xl md:text-3xl text-purple-100 font-bold">
                  A New Breed of Evolution
                </h3>
                <HoloCard className="w-full min-h-full md:min-h-full md:w-auto">
                  <div className="flex items-start gap-4 mb-4">
                    <Image
                      className="saturate-[0.7] brightness-125"
                      src="/docs/section-1/icon-dna.png"
                      alt="dna"
                      width={50}
                      height={50}
                    />
                    <p className="text-sm text-indigo-100/90">
                      You might be familiar with Charles Darwin, and if not, no
                      worries—this token is built on his theory of evolution. As
                      Darwin said,{" "}
                      <strong className="text-purple-400 font-black">
                        {" "}
                        "It is not the strongest of the species that survive,
                        nor the most intelligent, but the one most responsive to
                        change."
                      </strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-4 mb-4">
                    <Image
                      className="saturate-[0.7] brightness-125"
                      src="/docs/section-1/icon-timer.png"
                      alt="cat"
                      width={50}
                      height={50}
                    />
                    <p className="text-sm text-indigo-100/90">
                      CatDevAI reflects that adaptability. Every hour, this
                      creature releases a new DNA blueprint and competes in a
                      fierce race for dominance. It&apos;s not chaos for
                      fun—it's evolution in motion.
                    </p>
                  </div>

                  <div className="flex items-start gap-4 ">
                    <Image
                      className="saturate-[0.7] brightness-125"
                      src="/docs/section-1/icon-heart.png"
                      alt="predator"
                      width={50}
                      height={50}
                    />
                    <p className="text-sm text-indigo-100/90">
                      These digital cats don&apos;t seek approval; they face
                      judgment from predators. Weak DNAs vanish. Strong ones
                      rise, scarred and sharpened, ready to pass on the legacy.
                      <span className="float-end h-16 w-20 md:hidden flex"></span>
                    </p>
                  </div>
                </HoloCard>
              </div>
              <div className="relative -inset-y-16 inset-x-10 md:inset-0 lg:h-full flex items-center justify-center">
                <Image
                  className="scale-x-[-1] h-full w-auto saturate-[0.1] brightness-200"
                  src={"/docs/darwin-standing.png"}
                  width={300}
                  height={300}
                  alt="darwin teaching"
                />
              </div>
            </div>
          </div>

          <div
            ref={(el) => {
              panelsRef.current[1] = el;
            }}
            id="panel-2"
            className="w-full flex items-center justify-center text-4xl p-2 md:p-6 bg-transparent text-black"
          >
            <div className="w-auto h-full flex flex-col gap-2 md:gap-3 justify-center items-center">
              <OutlineHeading className="my-4 lg:max-w-screen-xl w-full  lg:text-6xl font-extrabold text-center uppercase tracking-widest">
                Let the Code Evolve, Survival of the Fittest
              </OutlineHeading>
              <div className="">
                <TokenEvolutionCard />
              </div>
            </div>
          </div>

          <div
            ref={(el) => {
              panelsRef.current[2] = el;
            }}
            id="panel-3"
            className="h-auto w-full flex flex-col items-center justify-center bg-transparent text-white"
          >
            <TokenLawsSection />
          </div>
        </div>
      </section>
    </>
  );
}
